import lodash from 'lodash';
import React, { useContext } from 'react';

import { RicosContext } from './../../ricos-context';
import type { ImageData, PluginContainerData } from './../../ricos-schema/next-schema';
import type { PluginProps } from './../../ricos-types';

import generalStyles from '../../editor-common/statics/styles/general-scss';
import generalRTLIgnoredStyles from '../../wix-rich-content-common/statics/styles/general.rtlignore-scss';

const stylesWithRTL = { ...generalStyles, ...generalRTLIgnoredStyles };

import classNames from 'classnames';

import type { PmImageNode } from './../../_shared/pm-node-types';

import classes from './figure-scss';

export const Figure: React.FC<PluginProps> = ({ NodeViewContent, ...props }) => {
  const { isMobile } = useContext(RicosContext);

  const pmNode = props.node.toJSON();
  const mainChild = pmNode.content[0] as PmImageNode;
  if (!mainChild) {
    return null;
  }
  const width = getContainerWidth(mainChild.attrs, isMobile);

  return (
    <div
      style={{ width: width ? `min(100%, ${width}px)` : undefined }}
      data-hook={`figure-${mainChild.type}`}
      className={getContainerClasses(mainChild.attrs.containerData)}
    >
      <NodeViewContent as="figure" className={classes.figure} />
    </div>
  );
};

const getContainerWidth = (
  nodeData: { image?: ImageData['image'] | null; containerData?: PluginContainerData | null },
  isMobile: boolean
) => {
  const disableCustomWidthMapper =
    nodeData?.image?.width &&
    isMobile &&
    (Number(nodeData?.containerData?.width?.custom) || 0) > 150;

  const disableCustomWidth = disableCustomWidthMapper ?? false;

  const customWidth = !disableCustomWidth ? nodeData?.containerData?.width?.custom : undefined;
  const originalWidth =
    nodeData?.containerData?.width?.size === 'ORIGINAL' || disableCustomWidth
      ? nodeData?.image?.width
      : undefined;
  const width = customWidth || originalWidth;
  return width;
};

const getContainerClasses = (containerData?: PluginContainerData | null) => {
  const alignment = containerData?.alignment;
  const alignmentClassName = alignment
    ? stylesWithRTL[`align${lodash.upperFirst(alignment.toLowerCase())}`]
    : undefined;

  const size = containerData?.width?.size;
  const sizeClassName =
    size && size !== 'FULL_WIDTH'
      ? stylesWithRTL[`size${lodash.upperFirst(size.toLowerCase())}`]
      : undefined;

  const textWrap = containerData?.textWrap;
  const textWrapClassName = textWrap ? undefined : stylesWithRTL.textWrapNowrap;

  return classNames(alignmentClassName, sizeClassName, textWrapClassName);
};
